<script setup>
	import { ref, computed } from 'vue';
	import { useForm, Head, usePage } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';
	import Swal from 'sweetalert2';

	const page = usePage();

	const props = defineProps({
		statuses: {
			type: Object,
			default: () => ({}),
		},
		skillsList: {
			type: Object,
			default: () => ({}),
		},
	});

	const emit = defineEmits(['toggle', 'close']);

	const loading = ref(false);
	const searchSkills = ref('');
	const searchCompetitors = ref('');
	const searchActivities = ref('');
	const searchDRs = ref('');
	const displayStructureAdd = ref(false);

	const form = useForm({
		status: null,
		first_name: null,
		last_name: null,
		role: 'contact',
		email: null,
		secondary_email: null,
		phone: null,
		secondary_phone: null,
		address: null,
		postal_code: null,
		city: null,
		pro_id: null,
		adeli: null,
		potential: null,
		targeted: false,
		objective: null,
		comment: null,
		schedule: null,
		skills: [],
		competitors: [],
		activities: [],
		dr: [],
		confirm_duplicate_contact: false,
		infos: {
			communication: [],
			visit: [],
		},
		structures: [],
	});
	const getDRUserById = (userId) => {
		return page.props.admin.users.find((user) => user.id == userId);
	};

	const searchSkill = computed(() => {
		if (searchSkills.value) {
			return page.props.str.statuses.filter((w) => w.name.toLowerCase().includes(searchSkills.value.toLowerCase()));
		}
		return page.props.str.statuses;
	});

	const searchCompetitor = computed(() => {
		if (searchCompetitors.value) {
			return page.props.str.statuses.filter((w) => w.name.toLowerCase().includes(searchCompetitors.value.toLowerCase()));
		}
		return page.props.str.statuses;
	});

	const searchActivity = computed(() => {
		if (searchActivities.value) {
			return page.props.str.statuses.filter((w) => w.name.toLowerCase().includes(searchActivities.value.toLowerCase()));
		}
		return page.props.str.statuses;
	});

	const searchDR = computed(() => {
		if (searchDRs.value) {
			return page.props.admin.users.filter(
				(w) =>
					w.first_name.toLowerCase().includes(searchDRs.value.toLowerCase()) ||
					w.last_name.toLowerCase().includes(searchDRs.value.toLowerCase()),
			);
		}
		return page.props.admin.users;
	});

	const handleSubmit = () => {
		loading.value = true;
		form.post(route('contacts.store'), {
			preserveScroll: true,
			onSuccess: (page) => {
				if (page.props.flash?.contact_id) {
					toast.success(page.props.flash.message);
				}
				if (page.props.flash?.type === 'duplicate_contact') {
					showDuplicateContactConfirmation(page.props.flash.contact);
				}
			},
			onFinish: () => {
				loading.value = false;
			},
		});
	};

	const showDuplicateContactConfirmation = (contact) => {
		const contactName = escapeHtml(contact.name);
		Swal.fire({
			title: 'Contact déjà existant',
			html: `${contactName} existe déjà, <a href="${contact.url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">cliquez ici</a> pour vérifier.<br>Êtes-vous sûr de vouloir créer ce nouveau contact ?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Créer quand même',
			cancelButtonText: 'Annuler',
		}).then((result) => {
			if (result.isConfirmed) {
				form.confirm_duplicate_contact = true;
				handleSubmit();
			}
		});
	};

	const escapeHtml = (value) => {
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	};

	const skillAdd = (skill) => {
		if (form.skills.includes(skill.id)) {
			toast.error('La profession est déjà présente dans la liste');
		} else {
			form.skills.push(skill.id);
		}
	};

	const DRAdd = (DR) => {
		if (form.dr.includes(DR.id)) {
			toast.error('Le DR est déjà présent dans la liste');
		} else {
			form.dr.push(DR.id);
		}
	};

	const activityAdd = (activity) => {
		if (form.activities.includes(activity.id)) {
			toast.error("L'activité est déjà présente dans la liste");
		} else {
			form.activities.push(activity.id);
		}
	};

	const competitorAdd = (competitor) => {
		if (form.competitors.includes(competitor.id)) {
			toast.error('Le concurrent est déjà présent dans la liste');
		} else {
			form.competitors.push(competitor.id);
		}
	};

	const structureAdd = (structure) => {
		if (!form.structures.some((s) => s.id === structure.structureRaw.id)) {
			if (structure.target && structure.status && structure.service) {
				let structuredStructure = { ...structure.structureRaw };
				let pivot = { structure_id: structure.target, status_id: structure.status, service_id: structure.service };
				structuredStructure.pivot = pivot;
				form.structures.push(structuredStructure);
			} else {
				toast.error('Tous les champs doivent être remplis');
			}
		} else {
			toast.error('Cette structure existe déjà');
		}
	};

	const skillDelete = (skill) => {
		form.skills = form.skills.filter((element) => element != skill);
	};

	const activityDelete = (activity) => {
		form.activities = form.activities.filter((element) => element != activity);
	};

	const competitorDelete = (competitor) => {
		form.competitors = form.competitors.filter((element) => element != competitor);
	};

	const structureDelete = (structure) => {
		form.structures = form.structures.filter((element) => element.id !== structure.id);
	};

	const DRDelete = (DR) => {
		form.dr = form.dr.filter((element) => element != DR);
	};
</script>

<template>
	<ui-modal @toggle="$emit('toggle')">
		<Head title="Création d'un contact" />
		<form method="post" @submit.prevent="handleSubmit" class="flex flex-col lg:grid lg:grid-cols-2 gap-2">
			<ui-title textSize="big" textPosition="left" class="lg:col-span-2 text-slate-700">
				<template v-slot:text>
					<p>Ajouter un contact</p>
				</template>
			</ui-title>
			<!-- TARGETED objective -->
			<span class="flex justify-between gap-2 items-center col-span-2">
				Cibler le contact ? :
				<ui-toggle v-model="form.targeted" :icons="['fa-crosshairs', 'fa-crosshairs']" />
			</span>
			<form-input placeholder="Prénom *" v-model="form.first_name"> </form-input>
			<form-input placeholder="Nom *" v-model="form.last_name"> </form-input>
			<form-input placeholder="Adresse" v-model="form.address" class="col-span-2"> </form-input>
			<form-input placeholder="Ville" v-model="form.city"> </form-input>
			<form-input placeholder="Code Postal" v-model="form.postal_code"> </form-input>
			<form-input placeholder="Téléphone" v-model="form.phone"> </form-input>
			<form-input placeholder="E-mail" v-model="form.email" type="mail"> </form-input>
			<form-input placeholder="Téléphone secondaire" v-model="form.secondary_phone"> </form-input>
			<form-input placeholder="E-mail secondaire" v-model="form.secondary_email" type="mail"> </form-input>
			<h3 v-if="page.props.auth.user.role == 'admin'" class="text-lg border-b-2 mt-2 col-span-2">DR(s)</h3>

			<!-- DROPDOWN ADD DR-->
			<ui-dropdown ref="dropdownTest" class="col-span-2">
				<template #content>
					<form-input placeholder="Recherche" v-model="searchDRs" class="w-full border-none"> </form-input>
					<div
						v-for="DR in searchDR"
						:key="DR.id"
						class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer flex gap-2 justify-center"
						@click="DRAdd(DR)"
						data-trigger="close">
						{{ DR.first_name }}
						{{ DR.last_name }}
					</div>
				</template>
				<template v-slot:trigger>
					<ui-button class="w-full">
						<template v-slot:icon>
							<i class="fas fa-plus-circle"></i>
						</template>
						<template v-slot:text>Ajouter</template>
					</ui-button>
				</template>
			</ui-dropdown>
			<!-- ACTIVITY LIST DR-->
			<div class="flex flex-col gap-3 justify-between col-span-2">
				<div class="flex flex-wrap gap-1">
					<div
						v-if="form.dr"
						v-for="DR in form.dr"
						:key="DR[1]"
						class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group text-xs"
						@click.prevent="DRDelete(DR)">
						<p v-if="getDRUserById(DR)">
							{{ getDRUserById(DR).first_name[0] + '.' }}
							{{ getDRUserById(DR).last_name }}
						</p>
					</div>
				</div>
			</div>
			<!-- CONTACT PREFERENCE -->
			<form-checkbox
				:items="page.props.str.statuses.filter((status) => status.type == 'communication')"
				v-model="form.infos.communication"
				label="Mode de communication"
				class="col-span-2">
			</form-checkbox>
			<!-- VISIT PREFERENCE -->
			<form-radio
				v-if="page.props.str.statuses.filter((status) => status.type == 'visit').length > 0"
				:items="page.props.str.statuses.filter((status) => status.type == 'visit')"
				v-model="form.infos.visit"
				label="Type de visites"
				class="col-span-2">
			</form-radio>
			<form-input placeholder="RPPS" v-model="form.pro_id" type="text" class="col-span-2"> </form-input>
			<form-input placeholder="ADELI" v-model="form.adeli" type="text" class="col-span-2"> </form-input>
			<form-input placeholder="Potentiel d'installation mensuel" v-model="form.potential" type="number" class="col-span-2"> </form-input>
			<form-input placeholder="Objectif de visite quadrimestriel" v-model="form.objective" type="number" class="col-span-2"> </form-input>
			<form-textarea placeholder="Commentaire" v-model="form.comment" class="col-span-2"> </form-textarea>
			<!-- HORAIRES -->
			<form-textarea placeholder="Horaires" v-model="form.schedule" class="col-span-2"> </form-textarea>
			<!-- SKILLS -->
			<h3 class="text-lg border-b-2 mt-2 col-span-2">Profession(s)</h3>
			<!-- DROPDOWN ADD SKILL -->
			<ui-dropdown ref="dropdownTest" class="col-span-2">
				<template #content>
					<form-input placeholder="Recherche" v-model="searchSkills" class="w-full border-none"> </form-input>
					<div
						v-for="skill in searchSkill.filter((status) => status.type == 'skill')"
						:key="skill.id"
						class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
						@click="skillAdd(skill)"
						data-trigger="close">
						{{ skill.name }}
					</div>
				</template>
				<template v-slot:trigger>
					<ui-button class="w-full">
						<template v-slot:icon>
							<i class="fas fa-plus-circle"></i>
						</template>
						<template v-slot:text>Ajouter</template>
					</ui-button>
				</template>
			</ui-dropdown>
			<!-- SKILL LIST -->
			<div class="flex flex-col gap-3 justify-between col-span-2">
				<div class="flex flex-wrap gap-1" v-if="form.skills">
					<div
						v-for="skill in form.skills"
						:key="skill"
						class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
						@click.prevent="skillDelete(skill)">
						{{ page.props.str.statuses.find((element) => element.id == skill)?.name }}
						<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
							<i class="fas fa-trash"></i>
						</div>
					</div>
				</div>
			</div>
			<!-- COMPETITION -->
			<h3 class="text-lg border-b-2 mt-2 col-span-2">Concurrent(s)</h3>
			<!-- DROPDOWN ADD COMPETITION -->
			<ui-dropdown :closeOnClick="false" ref="dropdownTest" class="col-span-2">
				<template v-slot:content>
					<form-input placeholder="Recherche" v-model="searchCompetitors" class="w-full border-none"> </form-input>
					<div
						v-for="competitor in searchCompetitor.filter((status) => status.type == 'competitor')"
						:key="competitor.id"
						class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
						@click="competitorAdd(competitor)"
						data-trigger="close">
						{{ competitor.name }}
					</div>
				</template>
				<template v-slot:trigger>
					<ui-button class="w-full">
						<template v-slot:icon>
							<i class="fas fa-plus-circle"></i>
						</template>
						<template v-slot:text>Ajouter</template>
					</ui-button>
				</template>
			</ui-dropdown>
			<!-- COMPETITION LIST-->
			<div class="flex flex-col gap-3 justify-between col-span-2">
				<div class="flex flex-wrap gap-1" v-if="form.competitors">
					<div
						v-for="competitor in form.competitors"
						:key="competitor"
						class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
						@click.prevent="competitorDelete(competitor)">
						{{ page.props.str.statuses.find((element) => element.id == competitor)?.name }}
						<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
							<i class="fas fa-trash"></i>
						</div>
					</div>
				</div>
			</div>
			<!-- ACTIVITY -->
			<h3 class="text-lg border-b-2 mt-2 col-span-2">Activité(s)</h3>
			<!-- DROPDOWN ADD ACTIVITY -->
			<ui-dropdown :closeOnClick="false" ref="dropdownTest" class="col-span-2">
				<template v-slot:content>
					<form-input placeholder="Recherche" v-model="searchActivities" class="w-full border-none"> </form-input>
					<div
						v-for="activity in searchActivity.filter((status) => status.type == 'activity')"
						:key="activity.id"
						class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
						@click="activityAdd(activity)"
						data-trigger="close">
						{{ activity.name }}
					</div>
				</template>
				<template v-slot:trigger>
					<ui-button class="w-full">
						<template v-slot:icon>
							<i class="fas fa-plus-circle"></i>
						</template>
						<template v-slot:text>Ajouter</template>
					</ui-button>
				</template>
			</ui-dropdown>
			<!-- ACTIVITY LIST -->
			<div class="flex flex-col gap-3 justify-between col-span-2">
				<div class="flex flex-wrap gap-1" v-if="form.activities">
					<div
						v-for="activity in form.activities"
						:key="activity"
						class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
						@click.prevent="activityDelete(activity)">
						{{ page.props.str.statuses.find((element) => element.id == activity)?.name }}
						<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
							<i class="fas fa-trash"></i>
						</div>
					</div>
				</div>
			</div>
			<!-- STRUCTURES -->
			<div class="flex flex-col col-span-2 gap-2">
				<h3 class="text-lg border-b-2">Structures</h3>
				<ui-button @click.prevent="displayStructureAdd = !displayStructureAdd">
					<template v-slot:icon>
						<i :class="displayStructureAdd ? 'fas fa-cancel' : 'fas fa-plus-circle'"></i>
					</template>
					<template v-slot:text> {{ displayStructureAdd ? 'Annuler' : 'Ajouter une structure' }} </template>
				</ui-button>
				<partial-add-target
					v-if="displayStructureAdd"
					search="structure"
					status="skill"
					service="service"
					:loading="false"
					@send="structureAdd($event)" />
				<ui-lists-structure
					v-if="form.structures?.length > 0"
					v-for="structure in form.structures"
					:key="structure.id"
					:structure="structure"
					@delete="structureDelete(structure)">
				</ui-lists-structure>
			</div>
			<form-validation-errors class="lg:col-span-2"></form-validation-errors>
			<ui-button class="col-span-2 mt-3 font-bold border-2" position="center" @click.prevent="handleSubmit()" :loading="loading">
				<template v-slot:text>Valider</template>
			</ui-button>
		</form>
	</ui-modal>
</template>
